const PENDING_PURCHASES_KEY = 'bab:ga4:pending-purchases';
const TRACKED_PURCHASES_KEY = 'bab:ga4:tracked-purchases';

let loadPromise = null;
let configuredMeasurementId = '';
const pendingEvents = [];

const isBrowser = typeof window !== 'undefined';
const SCRIPT_LOAD_TIMEOUT_MS = 5000;

const sanitizePath = (value) => {
  const input = String(value || '').trim();
  if (!input) return '/';
  try {
    const url = new URL(input, window.location.origin);
    return `${url.pathname || '/'}${url.search || ''}`;
  } catch (error) {
    return input.startsWith('/') ? input : `/${input}`;
  }
};

const sanitizeValue = (value) => {
  if (value === null || typeof value === 'undefined') return undefined;
  if (Array.isArray(value)) {
    const sanitized = value.map((item) => sanitizeValue(item)).filter((item) => typeof item !== 'undefined');
    if (!sanitized.length) return undefined;
    const containsObject = sanitized.some(
      (item) => item && typeof item === 'object' && !Array.isArray(item),
    );
    return containsObject ? sanitized : sanitized.join(', ');
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || undefined;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'object') {
    const sanitized = sanitizeParams(value);
    return Object.keys(sanitized).length ? sanitized : undefined;
  }
  return undefined;
};

const sanitizeParams = (params = {}) =>
  Object.fromEntries(
    Object.entries(params)
      .map(([key, value]) => [key, sanitizeValue(value)])
      .filter(([, value]) => typeof value !== 'undefined'),
  );

const readJsonStorage = (key) => {
  if (!isBrowser) return {};
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (error) {
    return {};
  }
};

const writeJsonStorage = (key, value) => {
  if (!isBrowser) return;
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    // Ignore storage failures; analytics should never block the booking flow.
  }
};

const getMeasurementId = () => configuredMeasurementId;

const flushPendingEvents = () => {
  if (!isBrowser || !window.gtag || !getMeasurementId()) return;
  while (pendingEvents.length) {
    const { eventName, params } = pendingEvents.shift();
    window.gtag('event', eventName, sanitizeParams(params));
  }
};

export const setAnalyticsMeasurementId = (value) => {
  const nextMeasurementId = String(value || '').trim();
  if (nextMeasurementId === configuredMeasurementId) return;

  configuredMeasurementId = nextMeasurementId;
  if (!configuredMeasurementId) {
    loadPromise = null;
    pendingEvents.length = 0;
    return;
  }

  void initAnalytics().then(() => {
    flushPendingEvents();
  });
};

export const analyticsEnabled = () => isBrowser && Boolean(getMeasurementId());

export const initAnalytics = () => {
  if (!analyticsEnabled()) return Promise.resolve(false);
  if (!isBrowser) return Promise.resolve(false);
  if (window.gtag && window.dataLayer) return Promise.resolve(true);
  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve) => {
    let timeoutId = null;

    const cleanup = () => {
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    const fail = () => {
      cleanup();
      loadPromise = null;
      resolve(false);
    };

    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function gtag() {
      window.dataLayer.push(arguments);
    };

    window.gtag('js', new Date());
    window.gtag('config', getMeasurementId(), {
      send_page_view: false,
      anonymize_ip: true,
    });

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(getMeasurementId())}`;
    script.onload = () => {
      cleanup();
      flushPendingEvents();
      resolve(true);
    };
    script.onerror = fail;

    try {
      document.head.appendChild(script);
    } catch (error) {
      fail();
      return;
    }

    timeoutId = window.setTimeout(() => {
      fail();
    }, SCRIPT_LOAD_TIMEOUT_MS);
  });

  return loadPromise;
};

export const trackEvent = (eventName, params = {}) => {
  if (!getMeasurementId()) {
    pendingEvents.push({ eventName, params });
    return;
  }
  void initAnalytics().then(() => {
    if (!window.gtag) return;
    window.gtag('event', eventName, sanitizeParams(params));
  });
};

export const trackPageView = (params = {}) => {
  if (!isBrowser) return;
  trackEvent('page_view', {
    page_title: document.title,
    page_path: sanitizePath(window.location.pathname),
    page_location: window.location.origin + sanitizePath(window.location.pathname),
    ...params,
  });
};

export const storePendingPurchase = (bookingRef, purchase) => {
  if (!bookingRef || !isBrowser) return;
  const purchases = readJsonStorage(PENDING_PURCHASES_KEY);
  purchases[bookingRef] = sanitizeParams(purchase);
  writeJsonStorage(PENDING_PURCHASES_KEY, purchases);
};

export const getPendingPurchase = (bookingRef) => {
  if (!bookingRef) return null;
  const purchases = readJsonStorage(PENDING_PURCHASES_KEY);
  return purchases[bookingRef] || null;
};

export const clearPendingPurchase = (bookingRef) => {
  if (!bookingRef || !isBrowser) return;
  const purchases = readJsonStorage(PENDING_PURCHASES_KEY);
  if (!purchases[bookingRef]) return;
  delete purchases[bookingRef];
  writeJsonStorage(PENDING_PURCHASES_KEY, purchases);
};

export const hasTrackedPurchase = (bookingRef) => {
  if (!bookingRef) return false;
  const tracked = readJsonStorage(TRACKED_PURCHASES_KEY);
  return tracked[bookingRef] === true;
};

export const markPurchaseTracked = (bookingRef) => {
  if (!bookingRef || !isBrowser) return;
  const tracked = readJsonStorage(TRACKED_PURCHASES_KEY);
  tracked[bookingRef] = true;
  writeJsonStorage(TRACKED_PURCHASES_KEY, tracked);
};
